import { useState } from 'react'

import useFileUpload, { IS_LOADING } from '@hooks/useFileUpload'
import { colors, fontSize, size } from '@styles/theme'
import useStateRef from 'react-usestateref'
import useLoading from '@hooks/useLoading'
import capitalize from '@utils/capitalize'
import { Toast } from '@base-ui/react'
import axios from '@utils/webRequests'
import styled from '@emotion/styled'

import { DialogFormGroup } from '@forms/profile_forms/SharedFormComponents'
import { SingleEnumRadio } from '@elements/form_elements/SingleEnumRadio'
import BackTopbar, { TopbarClearfix } from '@elements/BackTopbar'
import { DialogInput } from '@elements/dialog_inputs/DialogInput'
import VisuallyHiddenInput from '@elements/VisuallyHiddenInput'
import { TbAlertTriangle, TbArrowUp } from 'react-icons/tb'
import { PositionedTopProgress } from '@elements/Loaders'
import { PreviewImage } from '@elements/PreviewImage'
import ModalContainer from '@elements/ModalContainer'
import { FormTextField } from '@elements/Inputs'
import { ActionButton } from '../ActionButtons'
import { PiSpinnerBold } from 'react-icons/pi'

const ViolationListContainer = styled.div`
  display: flex;
  align-items: flex-start;
  flex-direction: column;

  label {
    margin: 0 !important;
    padding: 0 !important;
    justify-content: flex-start;
  }
`

const ViolationRadioList = ({ state, handleChange }) => {
  let violations = ['violence', 'hate_speech', 'suspected_bot', 'spamming_behavior', 'impersonation', 'suspected_underage', 'content_policy', 'illegal_activity']
  let radioList = []

  for (let i = 0; i < violations.length; i++) {
    radioList.push(
      <SingleEnumRadio
        onChange={handleChange}
        state={state}
        name={'violation'}
        label={capitalize(`${violations[i]}`.replaceAll('_', ' '))}
        value={`${violations[i]}`}
        key={i}
      />
    )
  }

  return (
    <DialogFormGroup>
      <ViolationListContainer>
        {radioList}
      </ViolationListContainer>
    </DialogFormGroup>
  )
}


const ReportForm = styled.form`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: ${size(3)};
  padding: ${size(3)};
  overflow-y: auto;
`

const DisplayText = styled.div`
  flex-shrink: 1;
  line-height: ${size(2.75)};
  font-size: ${fontSize('sm')};
  font-weight: 500;
`

const UploadButton = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  background-color: #333333;
  border: 0.5px solid rgba(255,255,255, 0.25);
  padding: ${size(1.5)};
  border-radius: 20px;
  font-size: ${fontSize('xs')};
  font-weight: bold;
`

const ActionButtonContainer = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  width: 100%;
  padding: ${size(1)} ${size(1.5)};
  background-color: black;
  height: ${size(8)};
`
  
const ActionButtonClearfix = styled.div`
  flex-shrink: 0;
  height: ${size(8)};
  width: 100%;
`

const LoaderText = styled.span`
  display: flex;
  align-items: center;
`

const AlertDisplay = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${size(2)};
  padding: ${size(1)};
  margin-bottom: ${size(2)};
  border-radius: 20px;
`

const defaultReportState = {
  violation: '',
  body: '',
}

const screenshotViolations = ['violence', 'hate_speech', 'impersonation', 'content_policy', 'illegal_activity']
const screenshotViolationsHash = screenshotViolations.reduce((acc, vio) => {
  acc[vio] = true
  return acc
}, {})

const ReportUser = ({ isOpen, setClosed, handlePromise, reportText='Report User', reportedUser, reportedItem, reportedType }) => {
  const [uploadButton, setUploadButton] = useStateRef(null)
  const { isLoading, isLoadingDelayed, setIsLoading } = useLoading()
  const toastManager = Toast.useToastManager()
  const [reportState, setReportState] = useState(defaultReportState)
    const {
    file,
    handleFileUpload: handleFileStateUpload,
    resetFileState,
    fileUrl,
    isFileLoaded,
  } = useFileUpload()

  const handleFileUpload = (e) => {
    setIsLoading(true)
    handleFileStateUpload(e)
      .then(() => e.target.value = null)
      .finally(() => setIsLoading(false))
  }

  const handleClear = (accessor) => {
    setReportState({ ...reportState, [accessor]: defaultReportState[accessor] })
  }

  const handleChange = (e) => {
    setReportState({ ...reportState, [e.target.name]: e.target.value  })
  }

  const requirementsNotMet = () => {
    let requirements = []
    if (!reportState?.violation) {
      requirements.push('violation')
    } else if (reportState?.violation in screenshotViolationsHash && !file) {
      requirements.push('screenshot')
    }
    return requirements
  }

  const userReportData = () => {
    const formData = new FormData()
    formData.append('reported_user_id', reportedUser?.uuid)
    formData.append('reportable_id', reportedItem?.uuid)
    formData.append('reportable_type', reportedType)
    formData.append('violation', reportState?.violation)
    formData.append('body', reportState?.body)
    formData.append('file', file)
    return formData
  }

  const handleReport = (e) => {
    e.preventDefault()
    const requirements = requirementsNotMet()
    if (requirements?.length > 0) {
      toastManager.add({ text: `${requirements[0]} required` })
      return
    }

    setIsLoading(true)
    axios.post(`/api/my/user_reports`, userReportData())
      .then((res) => {
        toastManager.add({ text: 'your report was sent' })
        setClosed()
        handlePromise?.(Promise.resolve(res))
      })
      .catch((err) => {
        console.log(err)
        toastManager.add({ text: 'failed to submit report' })
        handlePromise?.(Promise.reject(err))
      })
      .finally(() => {
        setIsLoading(false)
      })

  }

  const renderScreenshotReq = () => {
    if (reportState?.violation in screenshotViolationsHash) {
      return '*'
    }
  }

  const renderScreenshotText = () => (
    <>
      <span>
        Upload Screenshot&nbsp;
        {renderScreenshotReq()}
      </span>
      <TbArrowUp />
    </>
  )

  const renderScreenshotLoader = () => (
    <LoaderText>
      <PiSpinnerBold className='animation_rotation' />
      &nbsp;loading
    </LoaderText>
  )

  return(
    <ModalContainer
      open={isOpen}
      noHeader>
      <BackTopbar title={reportText} />
      <TopbarClearfix style={{ height: size(7) }} />
      <ReportForm onSubmit={handleReport}>
        <PositionedTopProgress isLoadingDelayed={isLoadingDelayed} />
        <div>
          <AlertDisplay>
            <TbAlertTriangle
              color={colors.tertiary(300)}
              size={size(6)}
              style={{
                marginLeft: size(2),
                flexShrink: 0
              }}
            />
            <DisplayText>
              Send a report for violations against Mandrake policies.
            </DisplayText>
          </AlertDisplay>
          <DialogInput
            slotProps={{ inputContainer: { style: { padding: size(1.5) } } }}
            label='Violation Type *'
            onClear={() => handleClear('violation')}
            displayValue={reportState?.violation && capitalize(reportState.violation.replaceAll('_', ' '))}>
            <ViolationRadioList state={reportState} handleChange={handleChange} />
          </DialogInput>
        </div>
        <div>
          <FormTextField
            required
            name='body'
            label='What are you reporting?'
            maxlength='200'
            placeholder='What happened?'
            value={reportState?.body}
            onChange={handleChange}
            style={{ marginBottom: size(0.75) }}
            multiline
            rows={5}
          />
          {!file && 
            <UploadButton onClick={() => uploadButton?.click?.()}>
              <VisuallyHiddenInput
                ref={node => node && setUploadButton(node)}
                type='file'
                accept='image/*'
                onChange={handleFileUpload}
              />
              {isFileLoaded === IS_LOADING 
                ? renderScreenshotLoader()
                : renderScreenshotText()
              }
            </UploadButton>
          }
          {file && 
            <PreviewImage
              resetFileState={resetFileState}
              fileUrl={fileUrl}
              file={file}
            />
          }
        </div>
        <ActionButtonClearfix />
        <ActionButtonContainer>
          <ActionButton
            style={{ opacity: isLoadingDelayed ? 0.6 : 1 }}
            disabled={isLoading}
            type='submit'
            onClick={handleReport}>
            Submit Report
          </ActionButton>
        </ActionButtonContainer>
      </ReportForm>
    </ModalContainer>
  )
}

export { ReportUser }
